import { PreviewCanvasModelInstance } from './previewCanvas';
import { PreviewSvgModelInstance } from './previewSvg';
import { BaseModel } from './_base/baseModel';
import { flow, types } from 'mobx-state-tree';
import { IUserImageAreaModel, UserImageAreaModel } from './userImageArea';
import { PreviewPrintModelInstance } from './previewPrint';
import { IProductModel } from './product';
import { IUserTextAreaModel, UserTextAreaModel } from './userTextArea';

export type PreviewModelInstance = PreviewSvgModelInstance | PreviewCanvasModelInstance | PreviewPrintModelInstance;

export const ViewBoxModel = types.model({
  x: types.optional(types.number, 0),
  y: types.optional(types.number, 0),
  width: types.optional(types.number, 0),
  height: types.optional(types.number, 0),
});

export const PreviewModel = BaseModel.named('PreviewModel')
  .props({
    productId: types.string,
    userImageAreas: types.optional(types.array(UserImageAreaModel), []),
    userTextAreas: types.optional(types.array(UserTextAreaModel), []),
  })
  .views(self => ({
    get isAnyUserImageUpdating(): boolean {
      return self.userImageAreas.some(userImageArea => userImageArea.userImage?.isUpdating === true);
    },
    get isAnyBlankSpace(): boolean {
      return self.userImageAreas.some(userImageArea => !userImageArea.hasUserImage);
    },
    get isAnyCropAlert(): boolean {
      return self.userImageAreas.some(userImageArea => userImageArea.userImage?.cropAlert);
    },
    get isMultiphoto(): boolean {
      return self.userImageAreas.length > 1;
    },
    get isMultitext(): boolean {
      return self.userTextAreas.length > 1;
    },
    get userImageArea(): IUserImageAreaModel {
      return self.userImageAreas[0];
    },
    get product(): IProductModel | undefined {
      return self.root.project.activeProject?.products?.find(product => product.id === self.productId);
    },
    get userTextArea(): IUserTextAreaModel {
      return self.userTextAreas[0];
    },
    get userImageAreasWithCropAlert(): IUserImageAreaModel[] {
      return self.userImageAreas.reduce((acc: IUserImageAreaModel[], userImageArea: IUserImageAreaModel) => {
        return !!userImageArea.userImageWithCropAlert ? [...acc, userImageArea] : acc;
      }, []);
    },
  }))
  .actions(self => ({
    cancelAll: flow(function* cancelAll() {
      return Promise.all(
        self.userImageAreas.map(userImageArea => {
          return userImageArea.userImage?.cancel();
        }),
      );
    }),
    // needed to apply filter to all images contemporarily - trying to improve performance but maybe it's required a new sdk method
    filterAll: flow(function* filterAll(filter: string) {
      return Promise.all(
        self.userImageAreas.map(userImageArea => {
          return userImageArea.userImage?.applyFilter(filter);
        }),
      );
    }),
    unloadAll: flow(function* unloadAll() {
      return Promise.all(
        self.userImageAreas.map(userImageArea => {
          return userImageArea.userImage?.unloadEditor();
        }),
      );
    }),
    selectAll: flow(function* selectAll() {
      return Promise.all(
        self.userImageAreas.map(userImageArea => {
          return userImageArea.userImage?.select();
        }),
      );
    }),
    refreshAll: flow(function* refreshAll() {
      return Promise.all(
        self.userImageAreas.map(userImageArea => {
          return userImageArea.userImage?.refreshEditor();
        }),
      );
    }),
  }));
